import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp, ConfigProvider, theme as antdTheme } from 'antd';
import type React from 'react';
import { useMemo } from 'react';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { BrowserRouter as Router } from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import { BORDERS, SPACING, TYPOGRAPHY, generateColorShadows } from './constants/design';
import { ThemeProvider, useColors } from './contexts/ThemeContext';
import { useAuthCrossTabSync, useProactiveTokenRefresh } from './hooks/useAuthSessionLifecycle';
import AppRouter from './router/AppRouter';

function AuthSessionEffects() {
  useAuthCrossTabSync();
  useProactiveTokenRefresh();
  return null;
}
import { useGlobalStore } from './stores/globalStore';
import { getFontFamilyFromCustomization, withAlpha } from './utils/colorUtils';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const AppContent: React.FC = () => {
  const { theme, customization } = useGlobalStore();
  const COLORS = useColors();
  const surfaceColors = COLORS.surface;
  const borderColors = COLORS.border;

  const getFontSize = () => {
    switch (customization.fontSize) {
      case 'small':
        return Number.parseInt(TYPOGRAPHY.fontSize.xs);
      case 'large':
        return Number.parseInt(TYPOGRAPHY.fontSize.md);
      default:
        return Number.parseInt(TYPOGRAPHY.fontSize.sm);
    }
  };

  const getFontFamily = () => {
    return getFontFamilyFromCustomization(customization.fontFamily, TYPOGRAPHY);
  };

  const getComponentSize = () => {
    if (customization.compactMode) return 'small';
    switch (customization.density) {
      case 'comfortable':
        return 'large';
      case 'compact':
        return 'small';
      default:
        return 'middle';
    }
  };

  const _colorShadows = generateColorShadows({
    primary: COLORS.primary.main,
    success: COLORS.success.main,
    error: COLORS.error.main,
    warning: COLORS.warning.main,
    info: COLORS.info.main,
  });

  const themeConfig = useMemo(
    () => ({
      algorithm: theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: COLORS.primary.main,
        colorSuccess: COLORS.success.main,
        colorWarning: COLORS.warning.main,
        colorError: COLORS.error.main,
        colorInfo: COLORS.info.main,

        colorText: COLORS.text.primary,
        colorTextSecondary: COLORS.text.secondary,
        colorTextTertiary: COLORS.text.tertiary,
        colorTextQuaternary: COLORS.text.disabled,

        colorBgContainer: surfaceColors.container,
        colorBgElevated: surfaceColors.elevated,
        colorBgLayout: surfaceColors.layout,

        colorBorder: borderColors.primary,
        colorBorderSecondary: borderColors.secondary,

        fontSize: getFontSize(),
        fontFamily: getFontFamily(),

        ...(customization.compactMode && {
          sizeStep: 3,
          sizeUnit: 3,
        }),
      },
      components: {
        Layout: {
          headerBg: surfaceColors.elevated,
          siderBg: surfaceColors.elevated,
          bodyBg: surfaceColors.layout,
        },
        Button: {
          borderRadius: Number.parseInt(BORDERS.radius.sm),
          fontWeight: TYPOGRAPHY.fontWeight.normal,
          colorPrimaryHover: COLORS.primary.dark,
          colorSuccessHover: COLORS.success.dark,
          colorErrorHover: COLORS.error.dark,
          colorWarningHover: COLORS.warning.dark,
          colorInfoHover: COLORS.info.dark,
          colorPrimaryActive: COLORS.primary.darker,
          colorSuccessActive: COLORS.success.darker,
          colorErrorActive: COLORS.error.darker,
          colorWarningActive: COLORS.warning.darker,
          colorInfoActive: COLORS.info.darker,
          controlOutline: withAlpha(COLORS.primary.main, 0.2),
          controlOutlineWidth: Number.parseInt(BORDERS.width.medium),
        },
        Card: {
          paddingLG: Number.parseInt(SPACING.lg),
          borderRadius: Number.parseInt(BORDERS.radius.md),
        },
        Input: {
          activeBorderColor: COLORS.primary.main,
          hoverBorderColor: COLORS.primary.light,
          controlOutline: withAlpha(COLORS.primary.main, 0.2),
        },
        Select: {
          activeBorderColor: COLORS.primary.main,
          hoverBorderColor: COLORS.primary.light,
          activeOutlineColor: withAlpha(COLORS.primary.main, 0.2),
          borderRadius: Number.parseInt(BORDERS.radius.md),
        },
        // Tag: Let Ant Design's algorithm handle all theme colors automatically
        // No overrides needed - algorithm adapts to both light and dark themes
        Table: {
          rowHoverBg: theme === 'dark' ? COLORS.neutral[800] : COLORS.neutral[50],
          headerBg: theme === 'dark' ? COLORS.neutral[800] : COLORS.neutral[50],
          borderColor: borderColors.secondary,
          headerSplitColor: borderColors.secondary,
        },
        Pagination: {
          itemActiveBg: COLORS.primary.main,
          itemActiveColor: COLORS.primary.contrastText,
          itemActiveColorHover: COLORS.primary.contrastText,
        },
        Menu: {
          fontSize: Number.parseInt(TYPOGRAPHY.fontSize.sm),
          fontWeight: TYPOGRAPHY.fontWeight.normal,
          itemActiveBg: COLORS.menu.selected,
          itemSelectedBg: COLORS.menu.selected,
          itemSelectedColor: theme === 'dark' ? undefined : COLORS.menu.selectedColor,
          itemHoverBg: COLORS.menu.hover,
          itemColor: theme === 'dark' ? COLORS.text.primary : undefined,
          itemSelectedBorderInlineEndWidth: 0,
          itemBorderRadius: 0,
          iconSize: Number.parseInt(TYPOGRAPHY.fontSize.md),
          iconMarginInlineEnd: Number.parseInt(SPACING.sm) + Number.parseInt(SPACING.xs),
          subMenuItemBg: 'transparent',
          itemDisabledColor: COLORS.text.disabled,
          controlOutline: withAlpha(COLORS.primary.main, 0.2),
          controlOutlineWidth: Number.parseInt(BORDERS.width.medium),
          popupBg: surfaceColors.elevated,
          zIndexPopup: 1060,
          dropdownWidth: 200,
          // Dark mode: use Ant Design's dark* tokens from our theme (avoids hardcoded #1677ff, #000c17)
          ...(theme === 'dark' && {
            darkItemBg: 'transparent',
            darkItemColor: COLORS.text.primary,
            darkItemHoverBg: COLORS.menu.hover,
            darkItemHoverColor: '#ffffff',
            darkItemSelectedBg: COLORS.menu.selected,
            darkItemSelectedColor: COLORS.menu.selectedColor,
            darkItemDisabledColor: COLORS.text.disabled,
            darkSubMenuItemBg: 'transparent',
            darkPopupBg: surfaceColors.elevated,
          }),
        },
      },
    }),
    [
      theme,
      COLORS,
      surfaceColors,
      borderColors,
      customization.compactMode,
      customization.fontSize,
      customization.fontFamily,
      customization.density,
    ],
  );

  const componentSize = useMemo(
    () => getComponentSize(),
    [customization.compactMode, customization.density],
  );
  const fontFamily = useMemo(() => getFontFamily(), [customization.fontFamily]);
  const fontSize = useMemo(() => getFontSize(), [customization.fontSize]);

  return (
    <ConfigProvider theme={themeConfig} componentSize={componentSize}>
      <AntdApp>
        <div
          style={{
            fontFamily: fontFamily,
            fontSize: `${fontSize}px`,
            lineHeight: TYPOGRAPHY.lineHeight.normal,
            height: '100%',
            overflow: 'hidden',
            fontFeatureSettings: '"ss01", "ss02", "cv01", "cv02"',
            textRendering: 'optimizeLegibility',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          }}
        >
          <HotkeysProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AuthSessionEffects />
              <AppRouter />
            </Router>
          </HotkeysProvider>
        </div>
      </AntdApp>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
